import * as React from "react"
import { 
  TextInput, 
  ReferenceInput, 
  SelectArrayInput, 
  NumberInput,
  BooleanInput,
  ArrayInput,
  SimpleFormIterator,
  useRecordContext,
  useGetList,
  AutocompleteArrayInput,
  AutocompleteInput,
  ImageInput, 
  ImageField
} from "react-admin"
import { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, Grid, Card, CardContent, Tabs, Tab, InputAdornment, Typography } from "@mui/material"
import { useParams } from "react-router-dom";
import isEqual from "lodash/isEqual";
import SpecWeights from "./SpecWeights"
import HexColorTextInput from "./HexColorTextInput";

const CategoryReferenceInput = () => {
  const { data, isLoading } = useGetList('product-categories', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'sorting', order: 'ASC' },
  });

  if (isLoading) return null;
  return (
    <AutocompleteArrayInput
      source="categories"
      choices={data}
      optionText="label"
      label="系列"
      isRequired
      disabled={isLoading}
    />
  );
};

const BasicInfoTab = () => {
  const record = useRecordContext();
  const { id } = useParams(); 
  const isEdit = !!id;
  
  const { setValue, getValues } = useFormContext();

  const bulkPrice = useWatch({ name: "bulkPrice" }) || "";
  const isLogoColor = useWatch({ name: "isLogoColor" });

  const prePriceRef = useRef("");

  useEffect(() => {
    if (isEqual(prePriceRef.current, bulkPrice)) return;
    
    prePriceRef.current = bulkPrice;

    const quantityDiscounts = getValues('quantityDiscounts')
    quantityDiscounts.forEach((item, index) => {
      const discountPercentage = ((bulkPrice - item.discountPrice) / bulkPrice) * 100;
      setValue(`quantityDiscounts.${index}.discountPercentage`, discountPercentage.toFixed(2));
      setValue(`quantityDiscounts.${index}.discountPrice`, bulkPrice);
    });
  }, [bulkPrice, getValues, setValue]);

  if (isEdit && !record) return;

  return (
    <Grid container width={{ xs: "100%", xl: 800 }} spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom mb={2}>
          基本資料
        </Typography>    
      </Grid>
      <Grid item xs={12} sm={8}>
        <TextInput source="code" label="編號(系統自動產生)" disabled />
      </Grid>
      <Grid item xs={12} sm={4} 
        sx={{
          display: "flex",
          justifyContent: "right"
        }}
      >
        <BooleanInput label="上架狀態" source="status" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput source="name" label="名稱" isRequired />
      </Grid>
      <Grid item xs={12} sm={6}>
        <NumberInput source="sorting" label="排序編號" isRequired />
      </Grid>
      <Grid item xs={12} sm={12}>
        <CategoryReferenceInput />
      </Grid>
      <Grid item xs={12} sm={6}>
        <NumberInput source="stock" label="庫存" />
      </Grid>
      {/* <Grid item xs={12} sm={6}>
        <NumberInput source="minOrderQuantity" label="最低下訂量" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <NumberInput source="maxOrderQuantity" label="最高下訂量" />
      </Grid> */}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom mb={2}>
          空白樣品
        </Typography>    
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput
          source="price"
          label="價格"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
          isRequired
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="blankSampleLeadTimeDays" label="交期天數" isRequired />
      </Grid>
      <Grid item xs={12} sm={4} sx={{ padding: 0 }}></Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="minOrderQuantity" label="數量選項區間最小值" isRequired />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="maxOrderQuantity" label="數量選項區間最大值" isRequired />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="incrementStep" label="數量選項區間值" isRequired />
      </Grid>
      <Grid item xs={12}>
        <SpecWeights fieldBase="blankSampleSpecifications" title="規格選項與分配權重（空白樣品）" />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom mb={2}>
          客製打樣
        </Typography>    
      </Grid>
      <Grid item xs={12} sm={6}>
        <NumberInput source="samplingTime" label="打樣所需時間(天)" isRequired />
      </Grid>
      <Grid item xs={12} sm={6}>
        <NumberInput source="samplingFee" label="打樣所需費用" isRequired />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="samplingMinOrderQuantity" label="數量選項區間最小值" isRequired />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="samplingMaxOrderQuantity" label="數量選項區間最大值" isRequired />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="samplingIncrementStep" label="數量選項區間值" isRequired />
      </Grid>
      <Grid item xs={12}>
        <SpecWeights fieldBase="samplingSpecifications" title="規格選項與分配權重（客製打樣）" />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom mb={2}>
          大宗採購
        </Typography>    
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput
          source="bulkPrice"
          label="大宗採購價格"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
          isRequired
        />
      </Grid>
      <Grid item xs={12} sm={8} sx={{ padding: 0 }}></Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="bulkMinOrderQuantity" label="數量選項區間最小值" isRequired />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="bulkMaxOrderQuantity" label="數量選項區間最大值" isRequired />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="bulkIncrementStep" label="數量選項區間值" isRequired />
      </Grid>
      <Grid item xs={12}>
        <SpecWeights fieldBase="bulkSpecifications" title="規格選項與分配權重（大宗採購）" />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom mb={2}>
          上傳規格說明圖片
        </Typography>    
      </Grid>
      <Grid item xs={12}>
        <ImageInput 
          source="sizeReferenceImage" 
          label={false} 
          accept={{ 'image/*': ['.png', '.jpg'] }} 
          sx={{
            "& .previews": {
              backgroundColor: "#f0f0f0", // 外層容器灰底
              padding: "10px", // 圖片周圍留白
              borderRadius: "4px", // 圓角效果
              border: "1px solid #ccc", // 灰色邊框
            },
          }}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom mb={2}>
          印製方式
        </Typography>    
      </Grid>
      <Grid item xs={12}>
        <ArrayInput source="printMethods" label={false}>
          <SimpleFormIterator getItemLabel={index => `#${index + 1}`}> 
            <ReferenceInput 
              source="printingMethodId" 
              reference="printing-methods" 
              sort={{ field: 'name', order: 'ASC' }}
              perPage={100}
            >
              <AutocompleteInput 
                label="印製方式" 
              />
            </ReferenceInput>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom mb={2}>
          Logo 設定
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}
        sx={{
          display: "flex",
          justifyContent: "right"
        }}
      >
        <BooleanInput label="編輯印製顏色" source="isLogoColor" />
      </Grid>
      <Grid item xs={12} sm={4}>
        <HexColorTextInput source="logoColor" label="Logo 顏色" disabled={!isLogoColor} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <NumberInput source="logoOpacity" label="Logo 透明度" min={0} max={1} step={0.01} disabled={!isLogoColor} />
      </Grid>
    </Grid>
  )
}

export default BasicInfoTab;